import * as React from "react";
import { Printer } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "convex/react";

import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth-client";
import { formatUtcDateOnly } from "@/lib/format";
import { calculatePalletCountForDelivery } from "@/lib/pallet-policy";
import { api } from "$convex/convexApi";
import { StorageBagTags } from "./StorageBagTags";
import "./page.css";

type AccessorialLabel = {
  key: string;
  label: string;
};

type CustomerStorageOrder = {
  kind?: "purchase" | "release";
  orderId: string;
  orderNumber: string;
  customerName: string;
  purchasedAt?: number;
  sandbox: boolean;
  fundingCurrent: boolean;
  holdStatus?: string;
  warehouseStatus: "pending" | "stored";
  items: Array<{
    orderItemId: string;
    cargoId: string;
    productName: string;
    quantity: number;
    remainingQuantity?: number;
    lotNumber?: string;
    locations: Array<{ palletId: string; name: string; quantity: number }>;
    tagsConfirmed: boolean;
    sourceOrderNumber?: string;
    arrivalReady?: boolean;
  }>;
};

function getAccessorialLabel(accessorial?: {
  description?: string | null;
  name?: string | null;
}): string | null {
  const rawLabel = accessorial?.description ?? accessorial?.name;
  if (typeof rawLabel !== "string") return null;
  const label = rawLabel.trim();
  return label.length > 0 ? label : null;
}

const ANTHEM_LOGO_URL =
  "https://cdn.prod.website-files.com/64493d11d62bbdf4ac3b43ef/644965713d613f2767b8f9fa_5da4ec2b220488c9b249d5dc_Anthem%20Logo_sq-01.webp";

function formatDate(timestamp?: number | string): string {
  if (!timestamp) return "N/A";
  const date = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatScheduledDate(timestamp?: number): string {
  if (!timestamp) return "N/A";
  return formatUtcDateOnly(timestamp, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatCityStateZip(city?: string, state?: string, zip?: string): string {
  if (!city && !state && !zip) return "-";
  return `${city || ""}, ${state || ""}, ${zip || ""}`
    .replace(/,\s*,/g, ",")
    .replace(/^,\s*/, "")
    .replace(/,\s*$/, "");
}

function getBagWeight(item: any): number {
  const productWeight = Number(item?.product?.weight ?? 0);
  if (Number.isFinite(productWeight) && productWeight > 0) return productWeight;
  const unitWeight = Number(item?.unitWeight ?? 0);
  if (Number.isFinite(unitWeight) && unitWeight > 0) return unitWeight;
  return 0;
}

export default function OrderResumePage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId") ?? searchParams.get("orderid");
  const orderNumber = searchParams.get("orderNumber") ?? searchParams.get("ordernumber");

  const { data: session, isPending: isAuthLoading } = useSession();
  const authUserId = (session?.user as { id?: string } | undefined)?.id;

  const orderData = useQuery(
    api.admin.getOrderWithFullDetails,
    !isAuthLoading && authUserId && (orderId || orderNumber)
      ? {
          authUserId,
          ...(orderId ? { orderId: orderId as any } : {}),
          ...(orderNumber ? { orderNumber } : {}),
        }
      : "skip",
  ) as any;

  const storageOrderId = orderData?._id;
  const storageData = useQuery(
    api.customerStorage.getForOrder,
    !isAuthLoading && authUserId && storageOrderId
      ? { orderId: storageOrderId }
      : "skip",
  ) as CustomerStorageOrder | null | undefined;

  const isLoading = Boolean(
    isAuthLoading ||
      (authUserId && (orderId || orderNumber) && orderData === undefined) ||
      (authUserId && storageOrderId && storageData === undefined),
  );

  const handlePrint = () => {
    window.print();
  };

  if (!orderId && !orderNumber) {
    return (
      <div className="order-resume-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>Order number or order ID is required. Use ?orderNumber=XXX or ?orderId=XXX</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="order-resume-page">
        <div className="loading-container">
          <p>Loading order data...</p>
        </div>
      </div>
    );
  }

  if (!authUserId) {
    return (
      <div className="order-resume-page">
        <div className="error-container">
          <h2>Authentication Required</h2>
          <p>Please sign in to view this order resume.</p>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="order-resume-page">
        <div className="error-container">
          <h2>Not Found</h2>
          <p>Order not found</p>
        </div>
      </div>
    );
  }

  if (orderData.status === "held_for_customer" && !storageData) {
    return (
      <div className="order-resume-page">
        <div className="error-container" role="alert">
          <h2>Storage details unavailable</h2>
          <p>Do not pick or ship. The customer storage record must be available before printing this DO.</p>
        </div>
      </div>
    );
  }

  const user = orderData.user;
  const items = orderData.items ?? [];
  const isStorageHold = Boolean(storageData) && orderData.status === "held_for_customer";
  const isStorageRelease = ["ready_to_prepare", "ready_for_pickup", "in_transit", "delivered"].includes(orderData.status);
  const canPrintBagTags = isStorageHold && storageData?.kind === "purchase" && storageData.fundingCurrent &&
    ["held_for_customer", "release_requested"].includes(storageData.holdStatus ?? "");
  const tagItems = storageData?.items.filter((item) => item.arrivalReady === true &&
    item.remainingQuantity !== undefined && item.remainingQuantity > 0) ?? [];
  if (searchParams.get("purpose") === "bag-tags") {
    if (!canPrintBagTags || !storageData) {
      return <main className="order-resume-page"><div className="error-container" role="alert">
        <h1>Bag tags unavailable</h1><p>Tags require a funded purchase held for the customer. Review this order before printing.</p>
      </div></main>;
    }
    if (!tagItems.length) {
      return <main className="order-resume-page"><div className="error-container" role="alert">
        <h1>No bags ready to tag</h1><p>Tags are available for arrived coffee still held for this customer.</p>
      </div></main>;
    }
    const tagProps = {
      orderId: storageData.orderId, orderNumber: storageData.orderNumber, customerName: storageData.customerName,
      purchasedAt: storageData.purchasedAt!, sandbox: storageData.sandbox,
      items: tagItems.map((item) => ({ orderItemId: item.orderItemId, productName: item.productName,
        lotNumber: item.lotNumber, remainingQuantity: item.remainingQuantity! })),
    };
    return <StorageBagTags key={JSON.stringify(tagProps)} {...tagProps} />;
  }
  const storageItemsById = new Map(
    storageData?.items.map((item) => [String(item.orderItemId), item]) ?? [],
  );
  const hasArrivedStorage = storageData?.items.some(item => item.arrivalReady !== false) === true;
  const storageAssignmentComplete = Boolean(storageData) &&
    storageData?.warehouseStatus === "stored" &&
    items.every((item: any) => {
      const storedItem = storageItemsById.get(String(item._id));
      if (!storedItem && storageData?.kind === "release") return true;
      return storedItem?.tagsConfirmed &&
        storedItem.quantity === Number(item.quantity) &&
        storedItem.locations.length > 0 &&
        storedItem.locations.every((location) => location.name.trim() && location.quantity > 0) &&
        storedItem.locations.reduce((sum, location) => sum + location.quantity, 0) === Number(item.quantity);
    });
  const delivery = orderData.delivery;
  const shippingInfo = orderData.shippingInfo;
  const accessorials = orderData.accessorials ?? [];
  const orderMeta = orderData?.metadata as
    | { phoneNumber?: string; additionalData?: { phoneNumber?: string } }
    | undefined;
  const accessorialNotificationPhone =
    orderMeta?.phoneNumber ?? orderMeta?.additionalData?.phoneNumber;
  const accessorialLabels = accessorials.reduce(
    (labels: AccessorialLabel[], accessorial: any, idx: number) => {
      const label = getAccessorialLabel(accessorial);
      if (!label) return labels;
      labels.push({
        key: String(accessorial?._id ?? `${label}-${idx}`),
        label:
          label === "Call Ahead" && accessorialNotificationPhone
            ? `${label} ${accessorialNotificationPhone}`
            : label,
      });
      return labels;
    },
    [],
  );

  const isPickup =
    delivery?.deliveryMethod === "pickup" || delivery?.deliveryMethod === "local_pickup";

  const shipViaLabel = isPickup
    ? "Local Pickup"
    : (delivery?.carrierName || delivery?.deliveryService || "TBD");

  const shippingDate = isStorageHold
    ? formatDate(orderData.createdAt ?? orderData._creationTime)
    : typeof delivery?.deliveryDate === "number"
    ? formatScheduledDate(delivery.deliveryDate)
    : typeof delivery?.pickupDate === "number"
      ? formatScheduledDate(delivery.pickupDate)
      : formatDate(orderData?.createdAt || orderData?._creationTime);

  const addressDisplay = shippingInfo
    ? formatCityStateZip(shippingInfo.city, shippingInfo.state, shippingInfo.postalCode)
    : formatCityStateZip(user?.city, user?.state, user?.postalCode);

  const addressString =
    shippingInfo?.address || user?.shippingAddress || user?.billingAddress || "No Address";

  const totalBags = items.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 0), 0);
  const totalLineWeight = items.reduce((sum: number, item: any) => {
    const qty = Number(item?.quantity) || 0;
    return sum + getBagWeight(item) * qty;
  }, 0);

  const palletWeight = 42;
  const palletCount = calculatePalletCountForDelivery(totalBags, delivery);
  const storagePalletCount = new Set(
    storageData?.items.flatMap((item) => item.locations.map((location) => location.palletId)) ?? [],
  ).size;

  return (
    <div className="order-resume-page">
      <div className="print-controls">
        <Button type="button" onClick={handlePrint} disabled={Boolean(storageData) && (!storageData?.fundingCurrent || (isStorageHold && !hasArrivedStorage) || (!isStorageHold && (!storageAssignmentComplete || !isStorageRelease)))}>
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
        {canPrintBagTags && storageData && tagItems.length > 0 ? <Button asChild variant="outline">
          <a href={`/order-resume?orderId=${encodeURIComponent(storageData.orderId)}&purpose=bag-tags`}>Print bag tags</a>
        </Button> : null}
      </div>

      <div className="order-container">
        <table className="main-table">
          <tbody>
            <tr>
              <td className="header-left">
                <img src={ANTHEM_LOGO_URL} alt="Anthem Logo" className="logo" />
              </td>
              <td className="header-right">
                <h1 className="title">{isStorageHold ? "CUSTOMER STORAGE DO" : "DELIVERY ORDER"}</h1>
                <p>Anthem Coffee Imports</p>
                <p>13621 Wyandotte Street</p>
                <p>Kansas City, MO 64145</p>
                <p>Phone: (913) 856 - 3480</p>
                <p>warehouse@anthemcoffeeimports.com</p>
              </td>
            </tr>
          </tbody>
        </table>

        <hr className="divider" />

        {storageData ? (
          <section className="storage-instructions" aria-label="Customer storage instructions">
            {storageData.sandbox ? <div><strong>TEST DO — DO NOT MOVE LIVE INVENTORY</strong></div> : null}
            <strong>
              {!storageData.fundingCurrent
                ? "Payment needs review — do not move this coffee"
                : isStorageHold
                ? hasArrivedStorage ? "Separate and hold; do not ship" : "Awaiting arrival — do not move this coffee"
                : !isStorageRelease
                  ? "Order is not released — do not pick or ship"
                : storageAssignmentComplete
                  ? "Pick customer-owned bags from the assigned locations below"
                  : "Storage assignment incomplete — do not pick or ship"}
            </strong>
            <div>{storageData.customerName}{storageData.kind !== "release" ? ` · Original order ${storageData.orderNumber}` : " · Purchase references shown per coffee"}</div>
            {isStorageHold && storageData.fundingCurrent ? (
              <div>Tag each arrived bag with the customer and original order number. Record pallet locations before confirming storage.</div>
            ) : null}
          </section>
        ) : null}

        <table className="info-table">
          <tbody>
            <tr>
              <td className="buyer-info">
                <p>
                  <strong>Buyer:</strong>
                </p>
                <p>{storageData ? storageData.customerName : user?.company || "No Company"}</p>
                <p>
                  Attn: {user?.firstName || ""} {user?.lastName || ""}
                </p>
                {!isStorageHold ? <><p>Address: {addressString}</p><p>{addressDisplay}</p></> : null}
                <p>Phone: {user?.mainPhoneNumber || "No Phone"}</p>
                <p>Email: {user?.email || "No Email"}</p>
              </td>
              <td className="order-info">
                <table className="order-details-table">
                  <tbody>
                    <tr>
                      <td className="order-label">Date :</td>
                      <td className="order-value">{shippingDate}</td>
                    </tr>
                    <tr>
                      <td className="order-label">SALES ORDER(S):</td>
                      <td className="order-value">{orderData.orderNumber || "No Order Number"}</td>
                    </tr>
                    <tr>
                      <td className="order-label">SHIPPER</td>
                      <td className="order-value">Anthem Coffee</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="products-table">
          <thead>
            <tr>
              {isStorageHold ? <th>PICK FROM</th> : null}
              <th>{isStorageHold ? "STORE FOR CUSTOMER" : "PICK FROM"}</th>
              <th>BAGS#</th>
              <th>ACI#</th>
              <th>WH#</th>
              <th>SALE#</th>
              <th>ITEM DESCRIPTION</th>
              <th>ORIGIN</th>
              <th>OCI#</th>
              <th>BAG WT.</th>
              <th>LINE WT.</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item: any) => {
              const qty = Number(item?.quantity) || 0;
              const bagWeight = getBagWeight(item);
              const storedItem = storageItemsById.get(String(item._id));
              const assigned = storedItem?.locations.reduce((sum, location) => sum + location.quantity, 0) ?? 0;
              const pickRows = storedItem?.arrivalReady === false ? [{ key: "awaiting", location: "Awaiting arrival", quantity: qty }] : storageData && (storedItem || storageData.kind !== "release")
                ? storedItem && assigned <= qty
                  ? [
                      ...storedItem.locations.map((location) => ({ key: location.palletId, location: location.name, quantity: location.quantity })),
                      ...(assigned < qty ? [{ key: "unassigned", location: "Assignment required", quantity: qty - assigned }] : []),
                    ]
                  : [{ key: "unassigned", location: "Assignment required", quantity: qty }]
                : [{ key: "spot", location: item?.cargo?.palletLocation || item?.cargo?.warehouse?.name || "", quantity: qty }];
              return pickRows.map((pick) => (
                <tr key={`${item._id || `${item.productId}-${item.cargoId}`}-${pick.key}`}>
                  {isStorageHold ? <td>{storedItem?.arrivalReady === false ? "Not received" : item?.cargo?.palletLocation || "Inventory location not recorded"}</td> : null}
                  <td>{pick.location}</td>
                  <td>{pick.quantity}</td>
                  <td>{item?.cargo?.contractId || ""}</td>
                  <td>{item?.cargo?.cargoUuid || ""}</td>
                  <td>{storedItem?.sourceOrderNumber ?? (storageData?.kind !== "release" && storageData ? storageData.orderNumber : orderData.orderNumber || "")}</td>
                  <td>{storedItem ? storedItem.productName : item?.product?.name || ""}</td>
                  <td>{item?.product?.origin?.name || ""}</td>
                  <td>{item?.cargo?.ociNumber || ""}</td>
                  <td>{bagWeight.toFixed(3)}</td>
                  <td>{(bagWeight * pick.quantity).toFixed(2)}</td>
                </tr>
              ));
            })}
            <tr>
              {isStorageHold ? <td></td> : null}
              <td></td>
              <td>{totalBags}</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td className="bold">Sub-Total</td>
              <td className="bold">{totalLineWeight.toFixed(2)}</td>
            </tr>
            {!isStorageHold ? <tr>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td className="bold">
                <div>Pallet Wt</div>
                <div>Total Wt</div>
              </td>
              <td className="bold">
                <div>{palletWeight * palletCount}</div>
                <div>{(totalLineWeight + palletWeight * palletCount).toFixed(2)}</div>
              </td>
            </tr> : null}
          </tbody>
        </table>

        {!isStorageHold ? <>
        <table className="carrier-section">
          <thead>
            <tr>
              <th colSpan={2}>CARRIER:</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <table className="carrier-details">
                  <tbody>
                    <tr>
                      <td className="carrier-label">Shipping Date:</td>
                      <td className="carrier-value">{shippingDate}</td>
                    </tr>
                    <tr>
                      <td className="carrier-label">Ship Via:</td>
                      {!isPickup ? (
                        <td className="carrier-value">
                          {delivery?.carrierLogoUrl ? (
                            <img
                              src={delivery.carrierLogoUrl}
                              alt={shipViaLabel}
                              className="carrier-logo"
                            />
                          ) : null}
                          {shipViaLabel}
                        </td>
                      ) : (
                        <td className="carrier-value">Local Pickup</td>
                      )}
                    </tr>
                    <tr>
                      <td className="carrier-label">Tracking #:</td>
                      <td className="carrier-value">
                        {delivery?.trackingNumber && delivery?.trackingUrl ? (
                          <a href={delivery.trackingUrl} target="_blank" rel="noopener noreferrer">
                            {delivery.trackingNumber}
                          </a>
                        ) : delivery?.trackingNumber ? (
                          delivery.trackingNumber
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="carrier-label">Confirmation:</td>
                      <td className="carrier-value">
                        {orderData.status === "confirmed" || orderData.status === "shipped"
                          ? "Confirmed"
                          : "Waiting"}
                      </td>
                    </tr>
                    <tr>
                      <td className="carrier-label">Quote #:</td>
                      <td className="carrier-value">{delivery?.quoteId || ""}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td>
                <table className="accessorials-details">
                  <thead>
                    <tr>
                      <th>ACCESSORIALS:</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="accessorial-content">
                        {accessorialLabels.map((accessorial: AccessorialLabel) => (
                          <div key={accessorial.key}>{accessorial.label}</div>
                        ))}
                        {orderData?.specialInstructions ? (
                          <div>Special Request: {orderData.specialInstructions}</div>
                        ) : null}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="notice-table">
          <tbody>
            <tr>
              <td>
                For BOLs, tracking, or truck-related questions including scheduling please email
                traffic@anthemcoffeeimports.com
              </td>
            </tr>
          </tbody>
        </table>

        </> : null}

        <table className="warehouse-section">
          <thead>
            <tr>
              <th colSpan={2}>Warehouse Instructions:</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="pallets-cell">
                <div className="pallets-label">{isStorageHold ? "STORAGE LOCATIONS" : "PALLETS"}</div>
                <div className="pallets-value">{isStorageHold ? storagePalletCount || "Assignment required" : palletCount}</div>
              </td>
              <td className="special-instructions">
                <div className="special-instructions-header">Special Instructions</div>
                <div className="special-instructions-content">
                  {isStorageHold && storageData?.fundingCurrent ? <strong>Separate customer-owned bags. Do not ship.</strong> : null}
                  {(!isStorageHold ? accessorialLabels : []).map((accessorial: AccessorialLabel) => (
                    <div key={`special-${accessorial.key}`}>{accessorial.label}</div>
                  ))}
                  {orderData?.specialInstructions ? (
                    <div>Special Request: {orderData.specialInstructions}</div>
                  ) : null}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

      </div>
    </div>
  );
}
